import React, { lazy, useContext, useEffect } from 'react';
import classNames from 'classnames';
import { Navigate } from 'react-router-dom';

import CheckoutHeader from 'components/Checkout/CheckoutHeader/CheckoutHeader';
import CheckoutNavbar from 'components/Checkout/CheckoutNavbar/CheckoutNavbar';
import CheckoutSummary from 'components/Checkout/CheckoutSummary/CheckoutSummary';
import ScrollToTopButton from 'components/Common/ScrollToTopButton/ScrollToTopButton';
import Loading from 'components/Common/Loading/Loading';
import { cartContext } from 'contexts/cartContext';
import { useCustomer } from 'hooks/useCustomer';
import { getRoutesUri } from 'services/generic';

import styles from './CheckoutLayout.module.scss';

const AfibelFooter = lazy(() => import('components/AfibelFooter/AfibelFooter'));

type Props = React.PropsWithChildren<{
  withSummary?: boolean;
  authenticate?: {
    must: boolean;
    redirect: string;
  };
  pageClassName?: (styles: Record<string, string>) => string;
}>;

export default function CheckoutLayout({
  withSummary = true,
  authenticate,
  children,
  pageClassName,
}: Props): JSX.Element {
  const { isLogged } = useCustomer();
  const { cart, isLoaded } = useContext(cartContext);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  if (
    isLoaded &&
    (!cart ||
      !cart.products.length ||
      !cart.isValidPrivilegeCode ||
      !!cart.proposedOfferedGifts.length)
  ) {
    return <Navigate to={getRoutesUri<string>('cart')} replace />;
  }

  if (authenticate && authenticate.must && false === isLogged) {
    return <Navigate to={authenticate.redirect} replace />;
  } else if (authenticate && !authenticate.must && true === isLogged) {
    return <Navigate to={authenticate.redirect} replace />;
  } else if (authenticate && null === isLogged) {
    return <Loading loading />;
  }

  const pageContent = (
    <div className={classNames(styles.page, pageClassName?.(styles))}>
      {children}
    </div>
  );

  return (
    <>
      <CheckoutHeader />
      <div className={styles.navWrapper}>
        <CheckoutNavbar />
      </div>
      <div
        className={classNames(styles.container, {
          [styles.noSummary]: !withSummary,
        })}
      >
        {pageContent}
        {withSummary && (
          <CheckoutSummary classes={{ wrapper: styles.summary }} />
        )}
      </div>
      <AfibelFooter />
      <ScrollToTopButton />
    </>
  );
}
